import Phaser from 'phaser';
import atlasIMG from 'img/atlas.png';
import {
  COLORS,
  CUSTOM_DOM_EVENTS,
  DEMO_MODE,
  DESKTOP_MODE
} from 'helpers/constants';
import SpriteButton from 'ui/spriteButton';
import SoundManager from 'states/components/SoundManager';
import {
  getPlayerCreatedLevelsByIDs,
  getSharedUgcMap
} from 'helpers/server_connection';
import { initializeControllerInput } from 'helpers/controller';
import { ifAutoPlaytest } from 'helpers/testHelpers';
import { login } from 'helpers/server_connection';
import castleIndexes from 'levelData/castleIndexes';

const getSharedLevelCodeFromPath = () => {
  const match = window.location.pathname.match(/\/play\/([^/?#]+)/);
  if (match && match[1]) return decodeURIComponent(match[1]);
  const params = new URLSearchParams(window.location.search);
  return params.get('share') || params.get('code') || '';
};
const sharedLevelBuild =
  typeof __SHARED_LEVEL_MODE__ !== 'undefined' && __SHARED_LEVEL_MODE__;

const toPlayableSharedUgcLevel = map => ({
  ...map,
  id: String(map.ugcLevelId),
  author: map.authorSteamName || String(map.authorUid || ''),
  combat: true,
  startingMusic:
    map.startingMusic || map.metadata?.startingMusic || 'music_battle',
  scoreLevelId: map.scoreLevelId,
  scoreLocalKey: map.scoreLocalKey,
  scoreMode: 'shared_freeplay'
});

const STEP_AUDIO_KEYS = [
  'concrete-8',
  'concrete-9',
  'concrete-3',
  'concrete-4',
  'concrete-5',
  'concrete-6',
  'grass-b',
  'grass-c',
  'grass-d',
  'grass-e',
  'grass-f',
  'grass-g'
];

const SHARED_LEVEL_BOOT_AUDIO_KEYS = [
  ...STEP_AUDIO_KEYS,
  'sfx_rollover',
  'sfx_click1',
  'sfx_click2',
  'sfx_door',
  'sfx_key',
  'sfx_unlock',
  'sfx_gate_creak',
  'sfx_gate_rumble',
  'sfx_switch_impact',
  'sfx_kill_piece',
  'sfx_battle_results',
  'sfx_coins',
  'music_battle',
  'music_game_over'
];

const ITEM_AUDIO_KEYS = {
  1: ['sfx_power_up', 'sfx_amulet_usage'],
  4: ['sfx_queens_fire'],
  5: ['sfx_seed_shot'],
  7: ['sfx_power_up', 'sfx_amulet_usage']
};

const SFX_ALIAS_TO_AUDIO_KEY = {
  click1: 'sfx_click1',
  click: 'sfx_click2',
  pointerover: 'sfx_rollover',
  door: 'sfx_door',
  key: 'sfx_key',
  unlock: 'sfx_unlock',
  switchImpact: 'sfx_switch_impact',
  gateCreak: 'sfx_gate_creak',
  gateRumble: 'sfx_gate_rumble',
  killPiece: 'sfx_kill_piece',
  newPartyMember: 'sfx_new_party_member',
  powerUp: 'sfx_power_up',
  battleResults: 'sfx_battle_results',
  chapterStart: 'sfx_chapter_1_start',
  amuletUsage: 'sfx_amulet_usage',
  forestSwitchOn: 'sfx_forest_switch_on',
  forestSwitchOff: 'sfx_forest_switch_off',
  seedShot: 'sfx_seed_shot',
  coins: 'sfx_coins',
  crash: 'sfx_crash',
  glassBreak: 'sfx_glass_break',
  queensFire: 'sfx_queens_fire',
  wolfHowl: 'sfx_wolf_howl',
  clothHeavy: 'sfx_cloth_heavy'
};

const getAudioAssets = musicExt => ({
  'concrete-8': `./assets/sfx/concrete-8.${musicExt}`,
  'concrete-9': `./assets/sfx/concrete-9.${musicExt}`,
  'concrete-3': `./assets/sfx/concrete-3.${musicExt}`,
  'concrete-4': `./assets/sfx/concrete-4.${musicExt}`,
  'concrete-5': `./assets/sfx/concrete-5.${musicExt}`,
  'concrete-6': `./assets/sfx/concrete-6.${musicExt}`,
  'grass-b': `./assets/sfx/grass-b.${musicExt}`,
  'grass-c': `./assets/sfx/grass-c.${musicExt}`,
  'grass-d': `./assets/sfx/grass-d.${musicExt}`,
  'grass-e': `./assets/sfx/grass-e.${musicExt}`,
  'grass-f': `./assets/sfx/grass-f.${musicExt}`,
  'grass-g': `./assets/sfx/grass-g.${musicExt}`,
  ambience_rain: `./assets/sfx/Ambiences/Thunder Rain loop1.${musicExt}`,
  thunder_clap: `./assets/sfx/Thunder clap.${musicExt}`,
  sfx_power_up: `./assets/sfx/Amulet_Power_up.${musicExt}`,
  sfx_amulet_usage: `./assets/sfx/Interaction sounds/Amulet_sound.${musicExt}`,
  sfx_forest_switch_on: `./assets/sfx/Interaction sounds/Forest_switch_on.${musicExt}`,
  sfx_forest_switch_off: `./assets/sfx/Interaction sounds/Forest_switch_off.${musicExt}`,
  sfx_seed_shot: `./assets/sfx/Interaction sounds/Seed_Shot_v2.${musicExt}`,
  sfx_chapter_1_start: `./assets/music/Chesslike_-_Chapterstart_Stingerv2.${musicExt}`,
  music_forest_battle: `./assets/music/Chesslike_-_Forest_Battle_Music.${musicExt}`,
  music_forest_town_destroyed: `./assets/music/Chesslike_-_Forest_Town_Destruction.${musicExt}`,
  music_enjoy_the_demo: `./assets/music/enjoy_the_demo_song.${musicExt}`,
  sfx_coins: `./assets/sfx/Coins.${musicExt}`,
  sfx_crash: `./assets/sfx/Crash.${musicExt}`,
  sfx_glass_break: `./assets/sfx/Glass_Break.${musicExt}`,
  sfx_queens_fire: `./assets/sfx/Queens_Fire_1.${musicExt}`,
  sfx_wolf_howl: `./assets/sfx/Wolf_Howl.${musicExt}`,
  sfx_new_party_member: `./assets/sfx/New_Party_Member.${musicExt}`,
  sfx_cloth_heavy: `./assets/sfx/cloth-heavy.${musicExt}`,
  sfx_rollover: `./assets/sfx/Interaction sounds/Rollover.${musicExt}`,
  sfx_click1: `./assets/sfx/Interaction sounds/Click1.${musicExt}`,
  sfx_click2: `./assets/sfx/Interaction sounds/Click2.${musicExt}`,
  sfx_door: `./assets/sfx/Interaction sounds/Door open.${musicExt}`,
  sfx_key: `./assets/sfx/Interaction sounds/Key.${musicExt}`,
  sfx_unlock: `./assets/sfx/Interaction sounds/Unlock Chest.${musicExt}`,
  sfx_gate_creak: './assets/sfx/temp/gate-creak.ogg',
  sfx_gate_rumble: './assets/sfx/temp/gate-rumble.wav',
  sfx_switch_impact: './assets/sfx/temp/switch-impact.wav',
  sfx_kill_piece: `./assets/sfx/Death Explosion1.${musicExt}`,
  music_main: `./assets/music/Chesslike-Title_Screen_2-2-21.${musicExt}`,
  music_town: `./assets/music/Chesslike_-_Town_Sketch_1-31-21.${musicExt}`,
  music_battle: `./assets/music/Chesslike-Orchestral Battlev1.${musicExt}`,
  music_gmas_basement: `./assets/music/Chesslike_-_GMA_Basement_Creepy.${musicExt}`,
  music_queens_fire: `./assets/music/Chesslike_-_Queen_Boss_V2.${musicExt}`,
  music_game_over: `./assets/music/Chesslike_-_Game_Over1.${musicExt}`,
  music_lofi_pondering: `./assets/music/Chesslike_-_lofi_pondering.${musicExt}`,
  music_forest: `./assets/music/Chesslike_-_Forest_Theme.${musicExt}`,
  music_forest_town: `./assets/music/Chesslike_Forest_Town_Sketch.${musicExt}`,
  music_forest_boss: `./assets/music/Chesslike_-_Forest_Boss.${musicExt}`,
  music_lofi_theme: `./assets/music/Lofi_Theme.${musicExt}`,
  music_night: `./assets/music/Chesslike_Night_Music_less_verb.${musicExt}`,
  sfx_battle_results: `./assets/music/Chesslike_-_Victory_Song_Short.${musicExt}`,
  ambience_town_bells: `./assets/sfx/Ambiences/Town Bells Ambience Loop.${musicExt}`,
  ambience_forest: `./assets/sfx/Ambiences/Forest Ambience Loop.${musicExt}`,
  ambience_town_birds: `./assets/sfx/Ambiences/Town Bird Ambience Loop.${musicExt}`,
  ambience_forest_night: `./assets/sfx/Ambiences/Forest_Night_Ambience.${musicExt}`
});

const getAudioKeyForSfxAlias = name => SFX_ALIAS_TO_AUDIO_KEY[name] || name;

const getSharedLevelAudioKeys = level => {
  const keys = new Set(SHARED_LEVEL_BOOT_AUDIO_KEYS);
  keys.add(level?.startingMusic || 'music_battle');
  keys.add(getAudioKeyForSfxAlias(level?.goalSound || 'door'));

  (level?.equippedItems || []).forEach(item => {
    const itemId = Number(item.item || item.itemID || item.id);
    (ITEM_AUDIO_KEYS[itemId] || []).forEach(key => keys.add(key));
  });

  return Array.from(keys);
};

export default class LoadingScene extends Phaser.Scene {
  constructor(test) {
    super({
      key: 'LoadingScene'
    });
    this.fileComplete = this.fileComplete.bind(this);
    this.fileProgress = this.fileProgress.bind(this);
    this.progress = this.progress.bind(this);
  }

  init() {
    this.scale.on('resize', this.resize);
    this.events.on('shutdown', () => {
      this.scale.off('resize', this.resize);
    });

    this.game.controllerInput = true;
    const [
      addControllerInputs,
      removeControllerInputs
    ] = initializeControllerInput(
      this,
      () => {},
      this.advance,
      this.advance,
      this.advance,
      this.advance,
      this.advance,
      this.advance,
      this.advance,
      this.advance,
      this.advance
    );

    this.addControllerInputs = addControllerInputs;
    this.removeControllerInputs = removeControllerInputs;

    addControllerInputs();

    this.input.on('pointermove', this.mousemove);

    if (sharedLevelBuild) {
      this.sharedLevelCode = getSharedLevelCodeFromPath();
      if (!this.sharedLevelCode) {
        this.sharedLevelError = 'Missing shared level code.';
        return;
      }

      getSharedUgcMap(this.sharedLevelCode)
        .then(map => {
          if (!map || map.error || map.message) {
            this.sharedLevelError =
              map?.error || map?.message || 'Shared level not found.';
            this.text.setText(this.sharedLevelError);
            return;
          }

          this.loadedLevel = toPlayableSharedUgcLevel(map);
          this.text.setText(
            `Loaded Level: ${map.name || this.sharedLevelCode}`
          );
          if (this.assetsLoaded) {
            this.renderPlayButton();
          }
        })
        .catch(error => {
          this.sharedLevelError = error?.message || String(error);
          this.text.setText(this.sharedLevelError);
        });
      return;
    }

    //load individual level
    const queryNumber = window.location.search.split('=')[1];
    const queryLevelID = parseInt(queryNumber, 10);
    if (queryLevelID) {
      this.deeplinkLevel = queryLevelID;
      getPlayerCreatedLevelsByIDs([queryLevelID]).then(response => {
        const rawMapData = response.maps[queryLevelID];
        if (rawMapData) {
          const metadata =
            rawMapData.metadata && JSON.parse(rawMapData.metadata);
          const equippedItems = metadata && metadata.equippedItems;
          this.loadedLevel = rawMapData;
          if (equippedItems) {
            this.loadedLevel.equippedItems = equippedItems;
          }
          this.text.setText(`Loaded Level: ${queryLevelID}`);
          if (this.assetsLoaded) {
            this.renderPlayButton();
          }
        }
      });
    }
  }

  resize = () => {
    const cam = this.cameras.main;
    const scale = this.game.defaultZoom;
    this.mainLogo
      .setPosition(cam.centerX, cam.centerY - 50 * scale)
      .setScale(scale);
  };

  mousemove = () => {
    if (this.game.controllerInput) {
      this.game.controllerInput = false;
    }
  };

  queueAudioAssets(keys) {
    const queued = new Set();
    keys.forEach(key => {
      const location = this.audioAssets && this.audioAssets[key];
      if (!location || queued.has(key) || this.cache.audio.exists(key)) {
        return;
      }
      queued.add(key);
      this.load.audio(key, [location]);
    });
  }

  ensureSharedLevelAudioLoaded() {
    if (!sharedLevelBuild || !this.loadedLevel) {
      return Promise.resolve();
    }

    const missingAudioKeys = getSharedLevelAudioKeys(this.loadedLevel).filter(
      key =>
        this.audioAssets &&
        this.audioAssets[key] &&
        !this.cache.audio.exists(key)
    );

    if (!missingAudioKeys.length) {
      return Promise.resolve();
    }

    this.text.setText('Loading level audio...');
    this.readyToPlay = false;
    return new Promise(resolve => {
      const onComplete = () => {
        this.load.off('complete', onComplete);
        this.readyToPlay = true;
        resolve();
      };
      this.load.once('complete', onComplete);
      this.queueAudioAssets(missingAudioKeys);
      this.load.start();
    });
  }

  preload() {
    this.cameras.main.setBackgroundColor('#0d1113');
    const scale = this.game.defaultZoom;
    this.loaderGroup = this.add.group();
    this.loaderBg = this.loaderGroup
      .create(
        this.cameras.main.centerX,
        this.cameras.main.centerY + 40 * scale,
        'loaderBg'
      )
      .setScale(scale);
    this.loaderBar = this.loaderGroup
      .create(
        this.cameras.main.centerX - 100 * scale,
        this.cameras.main.centerY + 45 * scale - 10 * scale,
        'loaderBar'
      )
      .setOrigin(0)
      .setScale(scale);
    this.text = this.add
      .text(
        this.cameras.main.centerX,
        this.cameras.main.centerY + 75 * scale,
        'Loading...',
        {
          fontSize: `${8 * scale}px`,
          padding: 10,
          fill: COLORS.LIGHT_BLUE,
          align: 'center',
          fontFamily: 'pixelMix'
        }
      )
      .setResolution(10)
      .setOrigin(0.5);
    this.loaderGroup.add(this.text);

    this.mainLogo = this.add
      .sprite(
        this.cameras.main.centerX,
        this.cameras.main.centerY - 50 * scale,
        'title_logo'
      )
      .setScale(scale);

    this.load.on('progress', this.progress);
    this.load.on('fileprogress', this.fileProgress);
    this.load.on('complete', this.fileComplete);

    //grades for battle results
    this.load.spritesheet(
      'ui_battle_results_a',
      './assets/images/ui/Battle Results/Campaign/Grade A-ani.png',
      {
        frameWidth: 42,
        frameHeight: 45
      }
    );
    this.load.spritesheet(
      'ui_battle_results_b',
      './assets/images/ui/Battle Results/Campaign/Grade B-ani.png',
      {
        frameWidth: 42,
        frameHeight: 45
      }
    );
    this.load.spritesheet(
      'ui_battle_results_c',
      './assets/images/ui/Battle Results/Campaign/Grade C-ani.png',
      {
        frameWidth: 42,
        frameHeight: 45
      }
    );
    this.load.spritesheet(
      'ui_battle_results_d',
      './assets/images/ui/Battle Results/Campaign/Grade D-ani.png',
      {
        frameWidth: 42,
        frameHeight: 45
      }
    );
    //objective updated
    this.load.image(
      'ui_objective_updated',
      './assets/images/ui/objective_updated.png'
    );
    //chapter start
    this.load.spritesheet(
      'ui_chapter_1_anim',
      './assets/images/title/Chapter Select/Chapter-Book-ani.png',
      {
        frameWidth: 187,
        frameHeight: 78
      }
    );
    //chapter start
    this.load.spritesheet(
      'ui_options_menu_button',
      './assets/images/title/Menu/settingsButton.png',
      {
        frameWidth: 39,
        frameHeight: 39
      }
    );
    //menu bg
    this.load.image(
      'title_bg_hills',
      './assets/images/title/title-screen-BGhills.png'
    );
    this.load.image(
      'title_bg_castle_back',
      './assets/images/title/title-screen-castleBack.png'
    );
    this.load.image(
      'title_bg_castle_front',
      './assets/images/title/title-screen-castleFront.png'
    );
    this.load.image(
      'title_bg_clouds',
      './assets/images/title/title-screen-Clouds.png'
    );
    this.load.image(
      'title_bg_fayette',
      './assets/images/title/title-screen-Fayette.png'
    );
    this.load.image(
      'title_bg_front_hill',
      './assets/images/title/title-screen-frontHill.png'
    );
    this.load.image(
      'title_bg_sky',
      './assets/images/title/title-screen-Sky.png'
    );
    this.load.image(
      'title_bg_mt',
      './assets/images/title/title-screen-Mountains.png'
    );

    this.load.image(
      'title_brass_bg',
      './assets/images/title/Menu/Menu-Brass-BG.png'
    );

    this.load.spritesheet(
      'title_new_game_btn',
      './assets/images/title/Menu/Menu-NewGame.png',
      {
        frameWidth: 151,
        frameHeight: 39
      }
    );
    this.load.spritesheet(
      'title_continue_btn',
      './assets/images/title/Menu/Menu-Continue.png',
      {
        frameWidth: 151,
        frameHeight: 39
      }
    );
    this.load.spritesheet(
      'title_delete_btn',
      './assets/images/title/Menu/Menu-Delete.png',
      {
        frameWidth: 100,
        frameHeight: 18
      }
    );
    this.load.spritesheet(
      'title_editor_btn',
      './assets/images/title/Menu/Menu-Editor.png',
      {
        frameWidth: 100,
        frameHeight: 29
      }
    );
    this.load.spritesheet(
      'title_versus_btn',
      './assets/images/title/Menu/Menu-Versus.png',
      {
        frameWidth: 100,
        frameHeight: 29
      }
    );
    this.load.image(
      'title_world_paint_btn',
      './assets/images/title/Menu/Menu-WorldPaint.png'
    );
    this.load.spritesheet(
      'title_freeplay_btn',
      './assets/images/title/Menu/Menu-Freeplay.png',
      {
        frameWidth: 100,
        frameHeight: 29
      }
    );

    //terrain
    this.load.spritesheet('master_atlas', atlasIMG, {
      frameWidth: 32,
      frameHeight: 32
    });

    this.load.spritesheet('small_items', './assets/images/items-small.png', {
      frameWidth: 11,
      frameHeight: 11
    });

    const idleAnims = [
      [
        'port_fayette_idle',
        './assets/images/animations/pieces/Fayette-idleani.png'
      ],
      ['port_gma_idle', './assets/images/animations/pieces/Gma-idleani.png'],
      ['port_sam_idle', './assets/images/animations/pieces/Sam-idleani.png'],
      ['port_Hp_idle', './assets/images/animations/pieces/Bknight-idleani.png'],
      ['port_Pp_idle', './assets/images/animations/pieces/Bpawn-idleani.png'],
      ['port_Bp_idle', './assets/images/animations/pieces/Bbish-idleani.png'],
      ['port_Rp_idle', './assets/images/animations/pieces/Brook-idleani.png'],
      ['port_Qp_idle', './assets/images/animations/pieces/Bqueen-idleani.png'],
      ['port_Kp_idle', './assets/images/animations/pieces/Bking-idleani.png'],
      ['port_H_idle', './assets/images/animations/pieces/Rknight-idleani.png'],
      ['port_P_idle', './assets/images/animations/pieces/Rpawn-idleani.png'],
      ['port_B_idle', './assets/images/animations/pieces/Rbish-idleani.png'],
      ['port_R_idle', './assets/images/animations/pieces/Rrook-idleani.png'],
      ['port_Q_idle', './assets/images/animations/pieces/Rqueen-idleani.png'],
      ['port_K_idle', './assets/images/animations/pieces/Rking-idleani.png'],
      [
        'port_Kgold_idle',
        './assets/images/animations/pieces/Rking-Story-idleani.png'
      ],
      [
        'port_Uncle_idle',
        './assets/images/animations/pieces/Uncle-idleani.png'
      ],
      [
        'port_raide_idle',
        './assets/images/animations/pieces/Raide-idleani.png'
      ],
      [
        'port_Qgold_idle',
        './assets/images/animations/pieces/Rqueen-Story-idleani.png'
      ]
    ];

    idleAnims.forEach(animConfig => {
      this.load.spritesheet(animConfig[0], animConfig[1], {
        frameWidth: 32,
        frameHeight: 64
      });
    });

    this.load.spritesheet(
      'uncle_movement',
      './assets/images/animations/pieces/Uncle-move.png',
      { frameWidth: 32, frameHeight: 64 }
    );

    this.load.spritesheet(
      'bishop_movement',
      './assets/images/animations/pieces/Bbish-move.png',
      { frameWidth: 32, frameHeight: 64 }
    );

    this.load.spritesheet(
      'fayette_movement',
      './assets/images/animations/pieces/Fayette-moveani.png',
      { frameWidth: 32, frameHeight: 64 }
    );

    this.load.spritesheet(
      'anim_torch',
      './assets/images/animations/torch.png',
      { frameWidth: 32, frameHeight: 32 }
    );

    this.load.spritesheet('anim_cat', './assets/images/animations/cat.png', {
      frameWidth: 15,
      frameHeight: 20
    });
    this.load.spritesheet(
      'anim_chicken',
      './assets/images/animations/chicken.png',
      {
        frameWidth: 18,
        frameHeight: 12
      }
    );

    this.load.spritesheet(
      'anim_birdhop',
      './assets/images/animations/birdhop.png',
      {
        frameWidth: 23,
        frameHeight: 19
      }
    );

    this.load.spritesheet(
      'anim_butterfly',
      './assets/images/animations/butterfly.png',
      {
        frameWidth: 40,
        frameHeight: 25
      }
    );

    this.load.spritesheet('anim_fire', './assets/images/animations/fire.png', {
      frameWidth: 32,
      frameHeight: 64
    });

    this.load.spritesheet(
      'anim_bonfire',
      './assets/images/animations/bonfire.png',
      {
        frameWidth: 19,
        frameHeight: 29
      }
    );

    this.load.spritesheet(
      'rain_drop',
      './assets/images/animations/rainsplash.png',
      {
        frameWidth: 10,
        frameHeight: 7
      }
    );

    this.load.spritesheet(
      'plant_grow',
      './assets/images/animations/plantgrow.png',
      {
        frameWidth: 32,
        frameHeight: 64
      }
    );

    this.load.spritesheet(
      'anim_switch_on',
      './assets/images/animations/switchclosed.png',
      {
        frameWidth: 32,
        frameHeight: 64
      }
    );

    this.load.spritesheet(
      'anim_switch_off',
      './assets/images/animations/switchopen.png',
      {
        frameWidth: 32,
        frameHeight: 64
      }
    );

    this.load.spritesheet(
      'anim_smoke_plume',
      './assets/images/animations/smoke-plume.png',
      {
        frameWidth: 34,
        frameHeight: 87
      }
    );

    this.load.spritesheet(
      'anim_foresteyes',
      './assets/images/animations/foresteyes.png',
      {
        frameWidth: 17,
        frameHeight: 8
      }
    );
    this.load.spritesheet(
      'anim_redflag',
      './assets/images/animations/Redflagwave.png',
      {
        frameWidth: 72,
        frameHeight: 59
      }
    );
    this.load.spritesheet('fire_bow', './assets/images/animations/bow.png', {
      frameWidth: 17,
      frameHeight: 18
    });

    this.load.spritesheet(
      'anim_teleport_in',
      './assets/images/animations/teleport-in-front.png',
      {
        frameWidth: 31,
        frameHeight: 640
      }
    );

    this.load.spritesheet(
      'anim_teleport_out_front',
      './assets/images/animations/teleport-out-front.png',
      {
        frameWidth: 31,
        frameHeight: 640
      }
    );

    this.load.spritesheet(
      'anim_teleport_out_back',
      './assets/images/animations/teleport-out-back.png',
      {
        frameWidth: 31,
        frameHeight: 640
      }
    );

    this.load.spritesheet(
      'anim_patzerton_flag_wave',
      './assets/images/animations/Redflagwave.png',
      {
        frameWidth: 72,
        frameHeight: 59
      }
    );

    this.load.spritesheet({
      key: '2020_units',
      url: atlasIMG,
      frameConfig: {
        frameWidth: 32,
        frameHeight: 64
      }
    });

    this.load.image('cursor_pointer_1x', './assets/images/ui/cursor.png');
    this.load.image('cursor_pointer_2x', './assets/images/ui/cursor2x.png');
    this.load.image('cursor_pointer_3x', './assets/images/ui/cursor3x.png');

    this.load.spritesheet(
      'amulet_defense_sprite',
      './assets/images/animations/ani-amulet.png',
      {
        frameWidth: 36,
        frameHeight: 67
      }
    );

    this.load.spritesheet(
      'classic_sprite',
      './assets/images/classic_sprite.png',
      { frameWidth: 64, frameHeight: 64 }
    );

    this.load.spritesheet(
      'red_piece_parts',
      './assets/images/pawn_pieces.png',
      {
        frameWidth: 15,
        frameHeight: 15
      }
    );

    this.load.image(
      'ui_gameover_bg',
      './assets/images/ui/Game Over/game_over.png'
    );

    this.load.spritesheet(
      'ui_gameover_overworld',
      './assets/images/ui/Game Over/Overworld.png',
      { frameWidth: 90, frameHeight: 13 }
    );
    this.load.spritesheet(
      'ui_gameover_freeplay',
      './assets/images/ui/Game Over/Freeplay.png',
      { frameWidth: 90, frameHeight: 13 }
    );
    this.load.spritesheet(
      'ui_gameover_quit',
      './assets/images/ui/Game Over/Quit.png',
      { frameWidth: 90, frameHeight: 13 }
    );
    this.load.spritesheet(
      'ui_gameover_retry',
      './assets/images/ui/Game Over/Retry battle.png',
      { frameWidth: 90, frameHeight: 13 }
    );
    this.load.spritesheet(
      'ui_gameover_undo',
      './assets/images/ui/Game Over/Undo last move.png',
      { frameWidth: 100, frameHeight: 13 }
    );

    this.load.spritesheet(
      'ui_editor_checkbox',
      './assets/images/ui/Editor/Checkbox.png',
      { frameWidth: 18, frameHeight: 18 }
    );
    this.load.spritesheet(
      'ui_editor_remove',
      './assets/images/ui/Editor/Remove.png',
      { frameWidth: 45, frameHeight: 45 }
    );
    this.load.image(
      'ui_editor_remove_cursor',
      './assets/images/ui/Editor/Remove_Cursor.png'
    );
    this.load.spritesheet(
      'ui_editor_plus',
      './assets/images/ui/Editor/Plus.png',
      { frameWidth: 56, frameHeight: 36 }
    );
    this.load.spritesheet(
      'ui_editor_minus',
      './assets/images/ui/Editor/Minus.png',
      { frameWidth: 56, frameHeight: 36 }
    );
    this.load.spritesheet(
      'ui_editor_erase_button',
      './assets/images/ui/Editor/Eraser.png',
      { frameWidth: 283, frameHeight: 54 }
    );
    this.load.spritesheet(
      'ui_editor_visibility',
      './assets/images/ui/Editor/Visibility.png',
      { frameWidth: 37, frameHeight: 26 }
    );
    this.load.image(
      'ui_editor_arrow_right',
      './assets/images/ui/Editor/Arrow-right.png'
    );
    this.load.spritesheet(
      'ui_editor_play',
      './assets/images/ui/Editor/Play.png',
      { frameWidth: 45, frameHeight: 48 }
    );
    this.load.spritesheet(
      'ui_editor_stop',
      './assets/images/ui/Editor/Stop.png',
      { frameWidth: 45, frameHeight: 48 }
    );

    this.load.spritesheet(
      'ui_editor_delete',
      './assets/images/ui/Editor/Delete-2.png',
      { frameWidth: 165, frameHeight: 48 }
    );
    this.load.spritesheet(
      'ui_editor_delete_large',
      './assets/images/ui/Editor/Delete-large.png',
      { frameWidth: 450, frameHeight: 56 }
    );
    this.load.spritesheet(
      'ui_dropdown_generic',
      './assets/images/ui/Editor/Music.png',
      { frameWidth: 452, frameHeight: 60 }
    );
    this.load.spritesheet(
      'ui_editor_menu',
      './assets/images/ui/Editor/Menu.png',
      { frameWidth: 94, frameHeight: 48 }
    );
    this.load.spritesheet(
      'ui_editor_save',
      './assets/images/ui/Editor/Save-2.png',
      { frameWidth: 94, frameHeight: 48 }
    );
    this.load.spritesheet(
      'ui_editor_save_large',
      './assets/images/ui/Editor/Save-large.png',
      { frameWidth: 217, frameHeight: 64 }
    );
    this.load.spritesheet(
      'ui_editor_publish',
      './assets/images/ui/Editor/Publish-large.png',
      { frameWidth: 450, frameHeight: 64 }
    );
    this.load.spritesheet(
      'ui_editor_exit',
      './assets/images/ui/Editor/Exit.png',
      { frameWidth: 450, frameHeight: 48 }
    );
    this.load.spritesheet(
      'ui_editor_load_large',
      './assets/images/ui/Editor/Load-large.png',
      { frameWidth: 217, frameHeight: 64 }
    );
    this.load.spritesheet(
      'ui_editor_duplicate_large',
      './assets/images/ui/Editor/Dupe-large.png',
      { frameWidth: 450, frameHeight: 48 }
    );
    this.load.spritesheet(
      'ui_editor_save_unsaved',
      './assets/images/ui/Editor/Save-unsaved-2.png',
      { frameWidth: 94, frameHeight: 48 }
    );
    this.load.spritesheet(
      'ui_editor_duplicate',
      './assets/images/ui/Editor/Duplicate.png',
      { frameWidth: 42, frameHeight: 45 }
    );
    this.load.spritesheet(
      'ui_editor_folder',
      './assets/images/ui/Editor/Folder.png',
      { frameWidth: 42, frameHeight: 45 }
    );
    this.load.spritesheet(
      'ui_editor_reset',
      './assets/images/ui/Editor/Reset.png',
      { frameWidth: 30, frameHeight: 33 }
    );
    this.load.spritesheet(
      'ui_editor_sim',
      './assets/images/ui/Editor/Sim.png',
      { frameWidth: 30, frameHeight: 33 }
    );
    this.load.spritesheet(
      'ui_arrow_large',
      './assets/images/ui/Editor/Arrow-updown-large.png',
      { frameWidth: 24, frameHeight: 20 }
    );
    this.load.spritesheet(
      'ui_arrow_small',
      './assets/images/ui/Editor/Arrow-updown-small.png',
      { frameWidth: 12, frameHeight: 10 }
    );
    this.load.image(
      'ui_palette_tab',
      './assets/images/ui/Editor/drawer handle.png'
    );
    this.load.spritesheet(
      'ui_back_button_editor',
      './assets/images/ui/Editor/Back Button Small.png',
      { frameWidth: 37, frameHeight: 39 }
    );

    this.load.spritesheet(
      'ui_waypoint',
      './assets/images/ui/waypointOffscreen.png',
      {
        frameWidth: 33,
        frameHeight: 33
      }
    );

    this.load.image('ui_crash', './assets/images/ui/Crash.png');

    this.load.image(
      'ui_end_demo_bullet',
      './assets/images/ui/End of Demo/Bullet.png'
    );

    this.load.image(
      'ui_end_demo_deathmate',
      './assets/images/ui/End of Demo/Deathmate.png'
    );

    this.load.image(
      'ui_end_demo_fayette',
      './assets/images/ui/End of Demo/Fayette w circle.png'
    );

    this.load.image(
      'ui_end_demo_thank_you',
      './assets/images/ui/End of Demo/Thank you.png'
    );

    this.load.spritesheet(
      'ui_end_demo_return_to_title',
      './assets/images/ui/End of Demo/Return to title.png',
      {
        frameWidth: 101,
        frameHeight: 31
      }
    );

    this.load.spritesheet(
      'ui_end_demo_whats_next',
      './assets/images/ui/End of Demo/Whats next.png',
      {
        frameWidth: 101,
        frameHeight: 31
      }
    );

    this.load.spritesheet(
      'ui_mini_pieces',
      './assets/images/ui/Battle Results/Mini Pieces.png',
      {
        frameWidth: 10,
        frameHeight: 20
      }
    );

    //story results
    this.load.image(
      'ui_story_results_bg',
      './assets/images/ui/Battle Results/Campaign/Background.png'
    );
    this.load.spritesheet(
      'ui_story_results_continue',
      './assets/images/ui/Battle Results/Campaign/Continue Button.png',
      {
        frameWidth: 73,
        frameHeight: 18
      }
    );
    this.load.spritesheet(
      'ui_story_results_retry',
      './assets/images/ui/Battle Results/Campaign/Retry Button.png',
      {
        frameWidth: 73,
        frameHeight: 18
      }
    );

    this.load.image(
      'ui_story_results_grade_a',
      './assets/images/ui/Battle Results/Campaign/Grade A.png'
    );
    this.load.image(
      'ui_story_results_grade_b',
      './assets/images/ui/Battle Results/Campaign/Grade B.png'
    );
    this.load.image(
      'ui_story_results_grade_c',
      './assets/images/ui/Battle Results/Campaign/Grade C.png'
    );
    this.load.image(
      'ui_story_results_grade_d',
      './assets/images/ui/Battle Results/Campaign/Grade D.png'
    );

    this.load.image('ui_button_9s', './assets/images/ui/Button9slice.png');
    this.load.image(
      'ui_button_9s_down',
      './assets/images/ui/Button9slice-down.png'
    );
    this.load.spritesheet('ui_box', './assets/images/ui/UI_tiles.png', {
      frameWidth: 6,
      frameHeight: 6
    });
    this.load.spritesheet(
      'ui_start_button',
      './assets/images/title/Menu/startButton.png',
      {
        frameWidth: 104,
        frameHeight: 38
      }
    );
    this.load.spritesheet(
      'ui_discord_button',
      './assets/images/ui/Discord Button.png',
      {
        frameWidth: 30,
        frameHeight: 34
      }
    );
    this.load.spritesheet(
      'ui_wishlist_button',
      './assets/images/ui/Wishlist.png',
      {
        frameWidth: 92,
        frameHeight: 31
      }
    );
    this.load.spritesheet(
      'ui_menu_button',
      './assets/images/ui/Menu Button.png',
      {
        frameWidth: 99,
        frameHeight: 39
      }
    );
    this.load.spritesheet(
      'ui_party_button',
      './assets/images/ui/Party Button.png',
      {
        frameWidth: 82,
        frameHeight: 26
      }
    );
    this.load.spritesheet(
      'ui_back_button',
      './assets/images/ui/Back Button.png',
      {
        frameWidth: 99,
        frameHeight: 39
      }
    );
    this.load.image(
      'ui_party_weapon_select_highlight',
      './assets/images/ui/Party UI/Weapon Select.png'
    );
    this.load.image('ui_tail', './assets/images/ui/UI_tail.png');
    this.load.image('ui_battle_start', './assets/images/ui/battle_start.png');
    this.load.image('ui_nameplate', './assets/images/ui/nameplate.png');

    // battle results
    this.load.image(
      'ui_battle_results',
      './assets/images/ui/Battle Results/Battle Results.png'
    );
    this.load.image(
      'ui_battle_captured',
      './assets/images/ui/Battle Results/captured.png'
    );
    this.load.image(
      'ui_battle_win',
      './assets/images/ui/Battle Results/Win.png'
    );
    this.load.image(
      'ui_battle_lose',
      './assets/images/ui/Battle Results/Lose.png'
    );
    this.load.image(
      'ui_battle_draw',
      './assets/images/ui/Battle Results/Draw.png'
    );
    this.load.image(
      'ui_title_mountains',
      './assets/images/ui/title_indicator_mountains.png'
    );
    this.load.image(
      'ui_title_mountains_no_obj',
      './assets/images/ui/title_indicator_mountains_small.png'
    );
    this.load.image(
      'ui_title_overworld',
      './assets/images/ui/title_indicator_overworld.png'
    );
    this.load.image(
      'ui_title_overworld_no_obj',
      './assets/images/ui/title_indicator_overworld_small.png'
    );
    this.load.image(
      'ui_title_home',
      './assets/images/ui/title_indicator_home.png'
    );
    this.load.image(
      'ui_title_forest',
      './assets/images/ui/title_indicator_forest.png'
    );
    this.load.image(
      'ui_title_forest_no_obj',
      './assets/images/ui/title_indicator_forest_small.png'
    );
    this.load.image(
      'ui_title_home_no_obj',
      './assets/images/ui/title_indicator_home_small.png'
    );
    this.load.image(
      'ui_title_castle',
      './assets/images/ui/title_indicator_castle.png'
    );
    this.load.image('ui_turns', './assets/images/ui/turn_indicator_gear.png');
    this.load.spritesheet('ui_flags', './assets/images/ui/turn_flags.png', {
      frameWidth: 28,
      frameHeight: 25
    });
    this.load.spritesheet(
      'ui_undo_last_turn',
      './assets/images/ui/turn_indicator_undo.png',
      {
        frameWidth: 76,
        frameHeight: 16
      }
    );
    this.load.image(
      'ui_item_indicator',
      './assets/images/ui/key_item_indicator.png'
    );
    this.load.image(
      'ui_gold_indicator',
      './assets/images/ui/gold_indicator.png'
    );

    // party/piece UI
    this.load.image(
      'ui_party_background',
      './assets/images/ui/Party UI/Party UI BG.png'
    );
    this.load.image(
      'ui_party_detail_panel',
      './assets/images/ui/Party UI/Right Panel.png'
    );
    this.load.image(
      'ui_party_make_leader_btn',
      './assets/images/ui/Party UI/Make Leader Button.png'
    );
    this.load.image(
      'ui_party_leader_indicator',
      './assets/images/ui/Party UI/Leader Cell Indicator.png'
    );
    this.load.spritesheet(
      'ui_party_weapons',
      './assets/images/ui/Party UI/Weapons.png',
      {
        frameWidth: 18,
        frameHeight: 20
      }
    );
    this.load.image(
      'ui_party_unit_cell',
      './assets/images/ui/Party UI/Unit Cell.png'
    );
    this.load.image(
      'ui_party_unit_cell_selected',
      './assets/images/ui/Party UI/Unit Cell-Selected.png'
    );
    this.load.image(
      'ui_party_weapon_select_bg',
      './assets/images/ui/Party UI/Weapon Selector BG.png'
    );

    // Party UI Piece Detail
    this.load.image(
      'ui_party_name_ribbon',
      './assets/images/ui/Party UI/Name Ribbon.png'
    );

    this.load.image(
      'ui_party_make_leader_button',
      './assets/images/ui/Party UI/Make Leader Button.png'
    );

    this.load.image(
      'ui_party_leader_button',
      './assets/images/ui/Party UI/Leader Button.png'
    );

    this.load.spritesheet(
      'ui_party_moveset_cells',
      './assets/images/ui/Party UI/Moveset Cells.png',
      {
        frameWidth: 10,
        frameHeight: 10
      }
    );

    // edit loadout
    this.load.image(
      'ui_edit_loadout_bg',
      './assets/images/ui/Edit Loadout/Loadout BG.png'
    );
    this.load.image(
      'ui_edit_loadout_bg_focus',
      './assets/images/ui/Edit Loadout/Loadout BG - Focus.png'
    );
    this.load.spritesheet(
      'ui_edit_loadout_start_button',
      './assets/images/ui/Edit Loadout/Start Button.png',
      {
        frameWidth: 136,
        frameHeight: 42
      }
    );
    this.load.spritesheet(
      'ui_edit_loadout_tile_bg',
      './assets/images/ui/Edit Loadout/Tile States.png',
      {
        frameWidth: 12,
        frameHeight: 12
      }
    );
    this.load.spritesheet(
      'ui_edit_loadout_mini_pieces',
      './assets/images/ui/Edit Loadout/Mini Pieces-Select.png',
      {
        frameWidth: 12,
        frameHeight: 20
      }
    );
    this.load.image(
      'ui_edit_loadout_party_bg',
      './assets/images/ui/Edit Loadout/Party BG.png'
    );
    this.load.image(
      'ui_edit_loadout_party_bg_focus',
      './assets/images/ui/Edit Loadout/Party BG - Focus.png'
    );
    this.load.image(
      'ui_edit_loadout_unit_cell_deployed',
      './assets/images/ui/Edit Loadout/Unit Cell-Deployed.png'
    );

    this.load.image('ui_piece_detail', './assets/images/ui/piece_detail.png');
    this.load.spritesheet(
      'ui_grid_pieces',
      './assets/images/ui/UI_grid_pieces.png',
      {
        frameWidth: 3,
        frameHeight: 3
      }
    );
    this.load.spritesheet('ui_hp_pip', './assets/images/ui/health_pip.png', {
      frameWidth: 7,
      frameHeight: 7
    });
    this.load.spritesheet(
      'ui_select_arrow',
      './assets/images/selected_arrow.png',
      {
        frameWidth: 20,
        frameHeight: 15
      }
    );
    this.load.image('ui_account_bg', './assets/images/ui/Account/brassBG.png');
    this.load.image(
      'ui_account_badge_novice',
      './assets/images/ui/Account/badges/novice.png'
    );
    this.load.spritesheet(
      'ui_account_login',
      './assets/images/ui/Account/login.png',
      {
        frameWidth: 62,
        frameHeight: 14
      }
    );
    this.load.spritesheet(
      'ui_account_logout',
      './assets/images/ui/Account/logOut.png',
      {
        frameWidth: 62,
        frameHeight: 14
      }
    );
    this.load.spritesheet(
      'ui_account_sound_off',
      './assets/images/ui/Account/soundOff.png',
      {
        frameWidth: 14,
        frameHeight: 15
      }
    );
    this.load.spritesheet(
      'ui_account_sound_on',
      './assets/images/ui/Account/soundOn.png',
      {
        frameWidth: 14,
        frameHeight: 15
      }
    );
    this.load.image(
      'ui_dialogue_with_portrait',
      './assets/images/ui/dialogue.png'
    );

    this.load.spritesheet(
      'ui_controller_buttons',
      './assets/images/ui/controller_buttons.png',
      { frameWidth: 23, frameHeight: 24 }
    );

    this.load.spritesheet(
      'ui_controller_buttons_sm',
      './assets/images/ui/controller_buttons_sm.png',
      { frameWidth: 12, frameHeight: 12 }
    );

    this.load.spritesheet(
      'ui_controller_buttons_xsm',
      './assets/images/ui/controller_buttons_xsm.png',
      { frameWidth: 9, frameHeight: 9 }
    );
    [
      ['ui_item_active_center', './assets/images/ui/item_active_center.png'],
      ['ui_item_active_lcap', './assets/images/ui/item_active_lcap.png'],
      ['ui_item_active_rcap', './assets/images/ui/item_active_rcap.png'],
      [
        'ui_item_active_rcap_hover',
        './assets/images/ui/item_active_rcap_hover.png'
      ]
    ].forEach(image => {
      this.load.image(image[0], image[1]);
    });

    this.load.spritesheet(
      'ui_item_frame',
      './assets/images/ui/item_frame.png',
      { frameWidth: 17, frameHeight: 18 }
    );

    this.load.image(
      'ui_joins_party_inner_stripe',
      './assets/images/ui/Joins Party/inner-stripe.png'
    );

    this.load.image(
      'ui_joins_party_outer_stripe',
      './assets/images/ui/Joins Party/outer-stripe.png'
    );

    this.load.image(
      'ui_joins_party_joins_party',
      './assets/images/ui/Joins Party/joins-party.png'
    );

    this.load.image(
      'ui_joins_party_name_bishoff',
      './assets/images/ui/Joins Party/name-bishoff.png'
    );

    this.load.image(
      'ui_joins_party_name_uncle',
      './assets/images/ui/Joins Party/name-uncle.png'
    );

    // freeplay ui
    this.load.image(
      'ui_freeplay_bg',
      './assets/images/title/Freeplay/BG-Freeplay.png'
    );

    this.load.spritesheet(
      'ui_freeplay_button_arrow_left',
      './assets/images/title/Freeplay/Button-ArrowLeft.png',
      { frameWidth: 21, frameHeight: 41 }
    );

    this.load.spritesheet(
      'ui_freeplay_button_arrow_right',
      './assets/images/title/Freeplay/Button-ArrowRight.png',
      { frameWidth: 21, frameHeight: 41 }
    );

    this.load.image(
      'ui_freeplay_story_level_frame',
      './assets/images/title/Freeplay/Button-StoryV2.png'
    );

    this.load.spritesheet(
      'ui_freeplay_button_replay_level',
      './assets/images/title/Freeplay/Replay-Level.png',
      { frameWidth: 94, frameHeight: 20 }
    );

    this.load.spritesheet(
      'ui_freeplay_button_play_level',
      './assets/images/title/Freeplay/Play-Level.png',
      { frameWidth: 94, frameHeight: 20 }
    );

    this.load.image(
      'ui_freeplay_leaderboard_bg',
      './assets/images/title/Freeplay/BG-Highscore.png'
    );

    this.load.spritesheet(
      'ui_freeplay_button_leaderboard',
      './assets/images/title/Freeplay/Leaderboard.png',
      { frameWidth: 94, frameHeight: 20 }
    );

    this.load.spritesheet(
      'ui_freeplay_grade',
      './assets/images/title/Freeplay/Sprite-Grade.png',
      { frameWidth: 42, frameHeight: 45 }
    );

    this.load.image(
      'ui_freeplay_badge_beat',
      './assets/images/title/Freeplay/Badge-Beat.png'
    );

    this.load.spritesheet(
      'ui_freeplay_button_level_small',
      './assets/images/title/Freeplay/Button-Level-Small.png',
      { frameWidth: 186, frameHeight: 45 }
    );

    this.load.spritesheet(
      'ui_freeplay_radio_popular',
      './assets/images/title/Freeplay/Radio-Popular.png',
      { frameWidth: 64, frameHeight: 10 }
    );

    this.load.spritesheet(
      'ui_freeplay_radio_recent',
      './assets/images/title/Freeplay/Radio-Recent.png',
      { frameWidth: 64, frameHeight: 10 }
    );

    this.load.spritesheet(
      'ui_freeplay_button_tab_community',
      './assets/images/title/Freeplay/Tab-Community.png',
      { frameWidth: 120, frameHeight: 35 }
    );

    this.load.spritesheet(
      'ui_freeplay_button_tab_player',
      './assets/images/title/Freeplay/Tab-Player.png',
      { frameWidth: 120, frameHeight: 35 }
    );

    this.load.spritesheet(
      'ui_freeplay_button_tab_story_replays',
      './assets/images/title/Freeplay/Tab-StoryReplays.png',
      { frameWidth: 120, frameHeight: 35 }
    );

    const portraits = {
      ui_port_Pf: './assets/images/ui/portraits/port-fayette.png',
      ui_port_Pf_down: './assets/images/ui/portraits/port-fayette-down.png',
      ui_port_Ps: './assets/images/ui/portraits/port-sam.png',
      ui_port_gma: './assets/images/ui/portraits/port-gma.png',
      ui_port_b_farmer: './assets/images/ui/portraits/port-farmer.png',
      ui_port_Pp: './assets/images/ui/portraits/port-bluepawn.png',
      ui_port_P: './assets/images/ui/portraits/port-redpawn.png',
      ui_port_Hp: './assets/images/ui/portraits/port-blueknight.png',
      ui_port_H: './assets/images/ui/portraits/port-redknight.png',
      ui_port_Bp: './assets/images/ui/portraits/port-bluebishop.png',
      ui_port_B: './assets/images/ui/portraits/port-redbishop.png',
      ui_port_Rp: './assets/images/ui/portraits/port-bluerook.png',
      ui_port_R: './assets/images/ui/portraits/port-redrook.png',
      ui_port_b_man1: './assets/images/ui/portraits/port-blueman1.png',
      ui_port_b_woman1: './assets/images/ui/portraits/port-bluewoman1.png',
      ui_port_Q: './assets/images/ui/portraits/port-queen.png',
      ui_port_Q_angry: './assets/images/ui/portraits/port-queen-angry.png',
      ui_port_r_king: './assets/images/ui/portraits/port-redking.png',
      ui_port_Rp_: './assets/images/ui/portraits/port-trainer.png',
      ui_port_uncle: './assets/images/ui/portraits/port-uncle.png',
      ui_port_r_bandit1: './assets/images/ui/portraits/port-redbandit1.png',
      ui_port_r_bandit2: './assets/images/ui/portraits/port-redbandit2.png',
      ui_port_r_bandit3: './assets/images/ui/portraits/port-redbandit3.png',
      ui_port_r_bandit4: './assets/images/ui/portraits/port-redbandit4.png',
      ui_port_wolf: './assets/images/ui/portraits/port-wolf.png',
      ui_port_bishoff: './assets/images/ui/portraits/port-bishoff.png',
      ui_port_raide: './assets/images/ui/portraits/port-raide.png',
      ui_port_raide_hurt: './assets/images/ui/portraits/port-raide-hurt.png',
      ui_port_elder: './assets/images/ui/portraits/port-elder.png',
      ui_large_port_uncle: './assets/images/ui/portraits/Large/Uncle.png',
      ui_large_port_bishoff: './assets/images/ui/portraits/Large/Bishoff.png'
    };

    Object.keys(portraits).forEach(imageKey => {
      this.load.image(imageKey, portraits[imageKey]);
    });

    //particles
    this.load.image('white_part', './assets/images/square.png');

    this.load.image('main_bg', './assets/images/map3x.png');

    this.load.image('phaser_logo', './assets/images/logos/phaser logo.png');

    this.load.image('ports_adam', './assets/images/ui/portraits/port-adam.png');
    this.load.image(
      'ports_brandon',
      './assets/images/ui/portraits/port-brandon.png'
    );
    this.load.image(
      'ports_brandon_red',
      './assets/images/ui/portraits/port-brandon-red.png'
    );
    this.load.image('ports_max', './assets/images/ui/portraits/port-max.png');

    const MUSIC_EXT = DEMO_MODE || DESKTOP_MODE ? 'ogg' : 'mp3';
    this.audioAssets = getAudioAssets(MUSIC_EXT);
    this.queueAudioAssets(
      sharedLevelBuild
        ? SHARED_LEVEL_BOOT_AUDIO_KEYS
        : Object.keys(this.audioAssets)
    );

    //overlays
    this.load.image('overlay_dark', './assets/images/overlays/dark.png');
    this.load.image('overlay_cloudy', './assets/images/overlays/cloudy.png');
    this.load.spritesheet(
      'overlay_light_mask',
      './assets/images/overlays/light_mask.png',
      {
        frameWidth: 545,
        frameHeight: 545
      }
    );
    this.load.spritesheet(
      'overlay_cloudy_anim',
      './assets/images/overlays/cloudy_ani.png',
      {
        frameWidth: 1280,
        frameHeight: 1280
      }
    );
    this.load.spritesheet(
      'overlay_smokey_m_anim',
      './assets/images/overlays/smoky-ani-multiply.png',
      {
        frameWidth: 1280,
        frameHeight: 1280
      }
    );
    this.load.spritesheet(
      'overlay_smokey_n_anim',
      './assets/images/overlays/smoky-ani-normal.png',
      {
        frameWidth: 1280,
        frameHeight: 1280
      }
    );
    this.load.image(
      'overlay_night_shade',
      './assets/images/overlays/dark5000.png'
    );

    //newForms
    this.load.html('login_form', './assets/html/login.html');
    this.load.html('zones_html', './assets/html/zones.html');
    this.load.html('lobby_chat', './assets/html/chat.html');
    this.load.html('rooms_list', './assets/html/rooms.html');
    this.load.html('select_dropdown', './assets/html/selectDropdown.html');
    this.load.html('title_input', './assets/html/titleInput.html');
    this.load.html('mp_check_input', './assets/html/mp-check.html');
    this.load.html('story_check_input', './assets/html/story-check.html');
  }

  fileProgress(file) {
    this.text.setText(`Loading ${file.key}`);
  }

  progress(progress) {
    this.loaderBar.scaleX = progress * this.game.defaultZoom;
  }

  fileComplete(progress) {
    this.load.off('progress', this.progress);
    this.load.off('fileprogress', this.fileProgress);
    this.load.off('complete', this.fileComplete);
    // set up audio tracks needed to proceed here: clicks, bgMusic
    this.game.soundManager = new SoundManager(this.game);

    this.loaderGroup.getChildren().forEach(child => {
      child.setAlpha(0);
    });

    if (this.deeplinkLevel) {
      this.assetsLoaded = true;
      this.text.setText(`Loading Level ${this.deepLinkLevel}`);
      if (this.loadedLevel) {
        this.text.setText(`Loaded Level: ${this.deepLinkLevel}`);
        //level loaded...
        this.renderPlayButton();
      }
    } else {
      this.renderPlayButton();
    }
  }

  startGame = async () => {
    this.removeControllerInputs();
    this.input.off('pointermove', this.mousemove);
    if (sharedLevelBuild) {
      if (!this.loadedLevel) {
        this.text.setText(this.sharedLevelError || 'Still loading level...');
        return;
      }
      await this.ensureSharedLevelAudioLoaded();

      this.scene.start('GameScene', {
        levelData: this.loadedLevel,
        freeplayHighscore: true,
        freeplayLevelIndex: String(this.loadedLevel.ugcLevelId),
        sharedLevelMode: true
      });
    } else if (this.deeplinkLevel) {
      const map = this.loadedLevel;
      if (castleIndexes.includes(this.deeplinkLevel.toString())) {
        this.scene.start('CastleScene', {
          index: this.deeplinkLevel
        });
      } else {
        this.scene.start('GameScene', {
          levelData: map
        });
      }
    } else {
      this.game.soundManager.playTrack('music_main', 'bgMusic');
      this.scene.start('OpeningCredits');
    }
  };

  advance = () => {
    if (this.readyToPlay) {
      this.startGame();
    }
  };

  async renderPlayButton() {
    window.focus();
    const scale = this.game.defaultZoom;
    this.readyToPlay = true;
    this.PlayButton = SpriteButton(this, {
      calcX: scene => scene.cameras.main.centerX,
      calcY: scene => scene.cameras.main.centerY + 53 * scale,
      sprite: 'ui_start_button',
      action: this.startGame
    })
      .setScale(this.game.defaultZoom)
      .setFrame(1);

    ifAutoPlaytest(async () => {
      const res = await login(window.dev.creds.user, window.dev.creds.pass);
      if (!res.error) {
        localStorage.setItem('chesslike-user', res.user_name);
        localStorage.setItem('chesslike-userid', res.user_id);
        localStorage.setItem('chesslike-token', res.user_token);

        window.dispatchEvent(
          new CustomEvent(CUSTOM_DOM_EVENTS.GameReady, {
            detail: {
              x:
                (this.PlayButton.x - this.cameras.main.worldView.x) *
                this.cameras.main.zoom,
              y:
                (this.PlayButton.y - this.cameras.main.worldView.y) *
                this.cameras.main.zoom
            }
          })
        );
      }
    });
  }
}
